import React, { useEffect, useMemo, useState } from "react";
import { Link, Routes, Route, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Briefcase, ClipboardList, ListChecks, UserCog, Filter, Plus,
  Loader2, AlertCircle, CheckCircle2, Clock, Tag, FileText, Calendar,
  BookOpenCheck, Star, MessageSquare, Sparkles, Shield, RefreshCw, AlertTriangle
} from "lucide-react";
import { inr } from "@/lib/format";
import ActivityTimeline from "@/components/business/ActivityTimeline";
import LoadingState from "@/components/business/LoadingState";
import MetricCard from "@/components/business/MetricCard";
import SearchBar from "@/components/business/SearchBar";
import BackButton from "@/components/business/BackButton";
import PageHeader from "@/components/business/PageHeader";

const ROLES = ["owner", "partner", "senior_ca", "associate", "article", "accountant"];
const ENTITY_TYPES = ["individual", "huf", "firm", "company", "llp", "trust"];
const TASK_STATUS = ["open", "in_progress", "blocked", "done"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

const COLUMN_ACCENT = {
  open:        "",
  in_progress: "border-l-4 border-l-blue-400",
  blocked:     "border-l-4 border-l-rose-400",
  done:        "border-l-4 border-l-emerald-400",
};
const COLUMN_COUNT_CLS = {
  open:        "bg-muted/60",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300",
  blocked:     "bg-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300",
  done:        "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300",
};
const PRIORITY_DOT = {
  urgent: "bg-rose-500",
  high:   "bg-amber-400",
  normal: "bg-slate-300 dark:bg-slate-600",
  low:    "bg-slate-200 dark:bg-slate-700",
};

const PRESET_TAGS = ["Salary", "Business", "NRI", "Capital Gains", "HUF", "Trust", "Professional", "Freelance", "Senior Citizen"];

// ================= Root =================
export default function Practice() {
  return (
    <Routes>
      <Route index element={<PracticeDashboard />} />
      <Route path="clients" element={<ClientsCRM />} />
      <Route path="clients/:clientId" element={<ClientDetail />} />
      <Route path="tasks" element={<TasksBoard />} />
      <Route path="workflows" element={<Workflows />} />
      <Route path="staff" element={<Staff />} />
    </Routes>
  );
}

// ================= Dashboard =================
function PracticeDashboard() {
  const [d, setD] = useState(null);
  const [rem, setRem] = useState({ upcoming: [], overdue: [] });
  const [risk, setRisk] = useState(null);
  useEffect(() => {
    api.get("/practice/dashboard").then(({ data }) => setD(data));
    api.get("/practice/reminders").then(({ data }) => setRem(data));
  }, []);
  useEffect(() => {
    if (d?.firm?.id) api.get(`/analytics/firm/${d.firm.id}/risk`).then(({ data }) => setRisk(data)).catch(() => {});
  }, [d]);

  if (!d) return <AppShell><LoadingState /></AppShell>;
  if (!d.firm) {
    return (
      <AppShell>
        <Card className="p-8 border-border max-w-xl mx-auto text-center">
          <Briefcase className="h-10 w-10 mx-auto text-muted-foreground" />
          <h2 className="mt-4 font-display text-2xl font-bold">You're not part of a firm yet</h2>
          <p className="mt-2 text-sm text-muted-foreground">Create your firm to unlock the CA workspace — clients CRM, staff roles, tasks and reporting.</p>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <PageHeader>
          <PageHeader.Title eyebrow={d.firm.name}>Practice</PageHeader.Title>
        </PageHeader>
        <p className="mt-1 text-sm text-muted-foreground">Firm workspace — clients, staff, tasks, workflows and compliance dashboards.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard label="Clients" value={d.clients_total} icon={Users} to="/practice/clients" testId="practice-stat-clients" />
          <MetricCard label="Staff" value={d.staff_total} icon={UserCog} to="/practice/staff" testId="practice-stat-staff" />
          <MetricCard label="Open tasks" value={d.tasks_open} icon={ClipboardList} to="/practice/tasks" testId="practice-stat-tasks" />
          <MetricCard label="Overdue" value={d.tasks_overdue} icon={AlertCircle} to="/practice/tasks?overdue=1" tone={d.tasks_overdue > 0 ? "danger" : "default"} testId="practice-stat-overdue" />
          <MetricCard label="Filed (linked)" value={d.filings_filed} icon={CheckCircle2} testId="practice-stat-filed" />
        </div>

        {risk && risk.scored_clients > 0 && (
          <Card className="mt-6 p-6 border-border" data-testid="firm-risk-widget">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-lg flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Firm-wide risk</div>
              <span className="text-xs text-muted-foreground">{risk.scored_clients}/{risk.total_clients} clients scored</span>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6">
              <div>
                <div className="font-display text-4xl font-bold">{risk.avg_score}<span className="text-lg text-muted-foreground">/100</span></div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">Avg score</div>
                <div className="mt-3 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between gap-6"><span>🟢 Low</span><span className="font-mono">{risk.bands.low}</span></div>
                  <div className="flex items-center justify-between gap-6"><span>🟡 Medium</span><span className="font-mono">{risk.bands.medium}</span></div>
                  <div className="flex items-center justify-between gap-6"><span>🔴 High</span><span className="font-mono">{risk.bands.high}</span></div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Highest risk</div>
                  <ul className="mt-2 space-y-1">
                    {(risk.highest_risk || []).length === 0 && <li className="text-xs text-muted-foreground">No scores yet.</li>}
                    {(risk.highest_risk || []).map((r, i) => (
                      <li key={i} className="text-xs flex items-center justify-between rounded border border-border px-2 py-1">
                        <span>{r.client_name}</span><span className="font-mono">{r.score}/100</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Biggest changes</div>
                  <ul className="mt-2 space-y-1">
                    {(risk.biggest_moves || []).length === 0 && <li className="text-xs text-muted-foreground">No significant changes.</li>}
                    {(risk.biggest_moves || []).map((r, i) => (
                      <li key={i} className="text-xs flex items-center justify-between rounded border border-border px-2 py-1">
                        <span>{r.client_name}</span>
                        <span className={`font-mono ${r.delta > 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.delta > 0 ? "▲" : "▼"} {Math.abs(r.delta)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Card className="p-6 border-border" data-testid="reminders-card">
            <div className="flex items-center justify-between">
              <div className="font-display font-semibold text-lg">Your reminders</div>
              <Link to="/practice/tasks" className="text-xs text-primary hover:underline">Task board →</Link>
            </div>
            {rem.overdue.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-rose-600 font-bold">Overdue ({rem.overdue.length})</div>
                <ul className="mt-2 space-y-2">
                  {rem.overdue.slice(0, 5).map(t => <TaskRow key={t.id} t={t} />)}
                </ul>
              </div>
            )}
            {rem.upcoming.length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Upcoming (7 days)</div>
                <ul className="mt-2 space-y-2">
                  {rem.upcoming.slice(0, 8).map(t => <TaskRow key={t.id} t={t} />)}
                </ul>
              </div>
            )}
            {rem.overdue.length === 0 && rem.upcoming.length === 0 && (
              <div className="mt-4 text-sm text-muted-foreground">No reminders. You're all caught up.</div>
            )}
          </Card>

          <Card className="p-6 border-border">
            <div className="font-display font-semibold text-lg">Firm workload</div>
            <div className="mt-4 space-y-2">
              {(d.workload || []).length === 0 && <div className="text-sm text-muted-foreground">No open tasks assigned yet.</div>}
              {(d.workload || []).map(w => (
                <div key={w.user_id} className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">{w.user_id?.slice(0, 8)}…</span>
                  <span className="font-semibold">{w.open_tasks} open</span>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline"><Link to="/practice/clients">+ Add client</Link></Button>
              <Button asChild size="sm" variant="outline"><Link to="/practice/tasks">+ New task</Link></Button>
              <Button asChild size="sm" variant="outline"><Link to="/practice/workflows">Apply workflow</Link></Button>
              <Button asChild size="sm" variant="outline"><Link to="/practice/staff">Manage staff</Link></Button>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}


function TaskRow({ t }) {
  return (
    <li className="text-sm flex items-center justify-between gap-2 rounded border border-border px-3 py-2">
      <div className="min-w-0">
        <div className="truncate font-medium">{t.title}</div>
        <div className="text-xs text-muted-foreground">Due {new Date(t.due_date).toLocaleDateString()} · {t.priority}</div>
      </div>
      <Badge variant="outline" className="text-[10px] uppercase">{t.status}</Badge>
    </li>
  );
}

// ================= Clients CRM =================
function ClientsCRM() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [entityType, setEntityType] = useState("");
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [savedFilters, setSavedFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crs_client_filters") || "[]"); } catch { return []; }
  });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (entityType) params.entity_type = entityType;
    if (tag) params.tag = tag;
    const { data } = await api.get("/practice/clients", { params });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, tag]);
  useEffect(() => { api.get("/practice/tags").then(({ data }) => setTags(data)); }, []);

  const saveFilter = () => {
    const name = window.prompt("Name this filter:");
    if (!name) return;
    const f = { name, q, entity_type: entityType, tag };
    const next = [...savedFilters, f];
    setSavedFilters(next);
    localStorage.setItem("crs_client_filters", JSON.stringify(next));
    toast.success("Filter saved");
  };
  const applyFilter = (f) => {
    setQ(f.q || ""); setEntityType(f.entity_type || ""); setTag(f.tag || "");
  };
  const deleteFilter = (name) => {
    const next = savedFilters.filter(x => x.name !== name);
    setSavedFilters(next);
    localStorage.setItem("crs_client_filters", JSON.stringify(next));
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/practice" label="Practice" />
        <PageHeader>
          <PageHeader.Title>Clients CRM</PageHeader.Title>
          <PageHeader.Actions>
            <NewClientDialog open={open} onOpenChange={setOpen} onCreated={load} />
          </PageHeader.Actions>
        </PageHeader>

        <Card className="mt-5 p-4 border-border">
          <div className="flex flex-wrap items-center gap-2">
            <SearchBar value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, PAN, email…" onSearch={load} className="flex-1 min-w-[220px]" inputClassName="h-10" data-testid="crm-search" />
            <Select value={entityType || "all"} onValueChange={(v) => setEntityType(v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 w-44"><SelectValue placeholder="Entity type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                {ENTITY_TYPES.map(e => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={tag || "all"} onValueChange={(v) => setTag(v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 w-44"><SelectValue placeholder="Tag" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {tags.map(t => <SelectItem key={t.tag} value={t.tag}>{t.tag} ({t.count})</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} disabled={busy}><Filter className="h-3.5 w-3.5 mr-1.5" />Search</Button>
            <Button variant="ghost" size="sm" onClick={saveFilter} disabled={!q && !entityType && !tag}><Star className="h-3.5 w-3.5 mr-1.5" />Save</Button>
          </div>
          {savedFilters.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {savedFilters.map(f => (
                <span key={f.name} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs">
                  <button className="font-medium" onClick={() => applyFilter(f)}>{f.name}</button>
                  <button className="text-muted-foreground hover:text-rose-500" onClick={() => deleteFilter(f.name)}>×</button>
                </span>
              ))}
            </div>
          )}
        </Card>

        <Card className="mt-5 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">PAN</th>
                <th className="text-left p-3">Entity</th>
                <th className="text-left p-3">Tags</th>
                <th className="text-left p-3">Filings</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No clients match.</td></tr>}
              {rows.map(c => (
                <tr key={c.id} className="border-t border-border hover:bg-muted/30" data-testid={`crm-row-${c.id.slice(0,6)}`}>
                  <td className="p-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.email || "—"}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{c.pan || "—"}</td>
                  <td className="p-3 capitalize">{c.entity_type}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(c.tags || []).map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                  </td>
                  <td className="p-3 num-rupee">{c.filings_count ?? 0}</td>
                  <td className="p-3 text-right">
                    <Button asChild variant="outline" size="sm"><Link to={`/practice/clients/${c.id}`} data-testid={`crm-open-${c.id.slice(0,6)}`}>Open</Link></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}

function NewClientDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", pan: "", email: "", phone: "", entity_type: "individual", tags: [], notes: "", linked_user_email: "" });
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  const addTag = (t) => { if (t && !f.tags.includes(t)) setF({ ...f, tags: [...f.tags, t] }); setTagInput(""); };
  const removeTag = (t) => setF({ ...f, tags: f.tags.filter(x => x !== t) });

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Client name is required");
    setBusy(true);
    try {
      const payload = { ...f };
      if (!payload.linked_user_email) delete payload.linked_user_email;
      if (!payload.pan) delete payload.pan;
      await api.post("/practice/clients", payload);
      toast.success("Client added");
      onOpenChange(false);
      setF({ name: "", pan: "", email: "", phone: "", entity_type: "individual", tags: [], notes: "", linked_user_email: "" });
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button data-testid="crm-add-client-btn"><Plus className="h-4 w-4 mr-1.5" /> Add client</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a new client</DialogTitle>
          <DialogDescription>CRM record — link to a taxpayer account any time.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-10 mt-1.5" data-testid="crm-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">PAN</Label><Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} className="h-10 mt-1.5 font-mono uppercase" maxLength={10} /></div>
            <div>
              <Label className="text-xs">Entity type</Label>
              <Select value={f.entity_type} onValueChange={(v) => setF({ ...f, entity_type: v })}>
                <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTITY_TYPES.map(x => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-10 mt-1.5" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-10 mt-1.5" /></div>
          </div>
          <div>
            <Label className="text-xs">Tags</Label>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {f.tags.map(t => <Badge key={t} variant="secondary" className="cursor-pointer" onClick={() => removeTag(t)}>{t} ×</Badge>)}
            </div>
            <div className="mt-1.5 flex gap-2">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput.trim()); } }} placeholder="Add a tag" className="h-9" />
              <Button type="button" size="sm" variant="outline" onClick={() => addTag(tagInput.trim())}>Add</Button>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {PRESET_TAGS.map(t => <button key={t} onClick={() => addTag(t)} className="text-[11px] rounded-full border border-border px-2 py-0.5 hover:bg-muted">{t}</button>)}
            </div>
          </div>
          <div><Label className="text-xs">Link to taxpayer email (optional)</Label><Input value={f.linked_user_email} onChange={(e) => setF({ ...f, linked_user_email: e.target.value })} className="h-10 mt-1.5" placeholder="taxpayer@example.com" /></div>
          <div><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5 min-h-[70px]" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="crm-save-client">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save client"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================= Client detail (timeline, notes, filings, tasks) =================
function ClientDetail() {
  const { clientId } = useParams();
  const [d, setD] = useState(null);
  const [intel, setIntel] = useState(null);
  const [intelBusy, setIntelBusy] = useState(false);
  const [noteText, setNoteText] = useState("");

  const load = () => api.get(`/practice/clients/${clientId}`).then(({ data }) => setD(data));
  const loadIntel = async () => {
    setIntelBusy(true);
    try { const { data } = await api.get(`/practice/clients/${clientId}/intel`); setIntel(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Intel failed"); }
    finally { setIntelBusy(false); }
  };
  useEffect(() => { load(); loadIntel(); /* eslint-disable-next-line */ }, [clientId]);

  if (!d) return <AppShell><LoadingState /></AppShell>;
  const { client, filings_by_ay, open_tasks, notes, timeline } = d;

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post(`/practice/clients/${clientId}/notes`, { text: noteText });
    setNoteText(""); load();
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/practice/clients" label="Clients" />
        <PageHeader>
          <PageHeader.Title eyebrow={client.entity_type} className="capitalize">
            <span data-testid="crm-client-name">{client.name}</span>
          </PageHeader.Title>
        </PageHeader>
        <div className="mt-1 text-sm text-muted-foreground">
          PAN <span className="font-mono uppercase">{client.pan || "—"}</span> · {client.email || "—"} · {client.phone || "—"}
        </div>
        <div className="mt-3 flex flex-wrap gap-1">
          {(client.tags || []).map(t => <Badge key={t} variant="outline"><Tag className="h-3 w-3 mr-1" />{t}</Badge>)}
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-5">
            <ClientIntelPanel intel={intel} loading={intelBusy} onRefresh={loadIntel} />

            <Card className="p-6 border-border">
              <div className="flex items-center justify-between">
                <div className="font-display font-semibold text-lg flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Filing history</div>
                <span className="text-xs text-muted-foreground">{filings_by_ay.length} assessment years</span>
              </div>
              <div className="mt-4 space-y-3">
                {filings_by_ay.length === 0 && <div className="text-sm text-muted-foreground">No linked filings. Link this client to a taxpayer account to see their filing history.</div>}
                {filings_by_ay.map(g => (
                  <div key={g.assessment_year} className="rounded border border-border">
                    <div className="bg-muted/40 px-3 py-2 flex items-center justify-between text-sm">
                      <span className="font-semibold">AY {g.assessment_year}</span>
                      <span className="text-xs text-muted-foreground">{g.filings.length} return(s)</span>
                    </div>
                    <div>
                      {g.filings.map(f => (
                        <div key={f.id} className="border-t border-border px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                          <div>
                            <span className="font-mono">{f.id.slice(0, 8)}</span> · {f.itr_type} · <span className="capitalize">{f.regime}</span>
                          </div>
                          <Badge variant="outline">{f.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6 border-border">
              <div className="font-display font-semibold text-lg flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" />Activity timeline</div>
              <div className="mt-4">
                <ActivityTimeline
                  events={timeline.map(e => ({ id: e.id, label: e.label, actor: e.actor_email, at: e.at }))}
                  emptyMessage="Timeline is empty. Notes, tasks and edits will appear here."
                />
              </div>
            </Card>
          </div>

          <div className="space-y-5">
            <Card className="p-5 border-border">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" />Internal notes</div>
              <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
                {notes.length === 0 && <div className="text-xs text-muted-foreground">No notes yet.</div>}
                {notes.map(n => (
                  <div key={n.id} className="text-sm border-l-2 border-primary/40 pl-3 py-1">
                    <div>{n.text}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{n.author_email} · {new Date(n.at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} className="min-h-[60px]" placeholder="Add a private note…" data-testid="crm-note-text" />
                <Button onClick={addNote} className="mt-2 w-full" variant="outline" size="sm" data-testid="crm-add-note">Add note</Button>
              </div>
            </Card>

            <Card className="p-5 border-border">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" />Open tasks</div>
              <div className="mt-3 space-y-2">
                {open_tasks.length === 0 && <div className="text-xs text-muted-foreground">No open tasks for this client.</div>}
                {open_tasks.map(t => (
                  <div key={t.id} className="rounded border border-border p-2 text-sm">
                    <div className="font-medium">{t.title}</div>
                    <div className="text-[11px] text-muted-foreground">Due {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"} · {t.priority}</div>
                  </div>
                ))}
              </div>
              <Button asChild size="sm" variant="outline" className="mt-3 w-full"><Link to="/practice/tasks">Open task board →</Link></Button>
            </Card>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ================= Client Intelligence Panel =================
const RISK_STYLES = {
  low: { bg: "bg-emerald-50", ring: "ring-emerald-200", pill: "bg-emerald-500", label: "text-emerald-700", dot: "🟢" },
  medium: { bg: "bg-amber-50", ring: "ring-amber-200", pill: "bg-amber-500", label: "text-amber-700", dot: "🟡" },
  high: { bg: "bg-rose-50", ring: "ring-rose-200", pill: "bg-rose-500", label: "text-rose-700", dot: "🔴" },
};

function ClientIntelPanel({ intel, loading, onRefresh }) {
  if (loading && !intel) {
    return (
      <Card className="p-6 border-border">
        <LoadingState size="sm" message="Building intelligence report…" className="py-0 justify-start" />
      </Card>
    );
  }
  if (!intel || intel.error) return null;
  const risk = RISK_STYLES[intel.risk_band || intel.risk_score || "low"];
  const h = intel.history || {};
  const anomalies = intel.anomalies || [];
  const recs = intel.recommendations || [];
  const score = intel.risk_value ?? 0;
  const delta = intel.risk_delta;

  return (
    <Card className={`p-6 border-border ring-1 ${risk.ring} ${risk.bg}`} data-testid="client-intel-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`grid h-8 w-8 place-items-center rounded-full ${risk.pill} text-white`}>
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">Client Intelligence</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {intel.ai_meta?.provider}/{intel.ai_meta?.model} · {intel.ai_meta?.prompt_version}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className={`font-display text-3xl font-bold ${risk.label}`} data-testid="risk-score-value">
              {score}<span className="text-lg text-muted-foreground">/100</span>
            </div>
            <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${risk.label}`}>
              {risk.dot} {intel.risk_band || intel.risk_score}
              {typeof delta === "number" && delta !== 0 && (
                <span className={`ml-2 ${delta > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                  {delta > 0 ? "▲" : "▼"} {Math.abs(delta)}
                </span>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading} data-testid="intel-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      {intel.ai_narrative && (
        <div className="mt-4 rounded border border-border bg-background p-4 text-sm leading-relaxed">
          {intel.ai_narrative}
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Years filed" value={h.years_filed ?? 0} />
        <MiniStat label="Avg refund" value={`₹${(h.avg_refund || 0).toLocaleString()}`} />
        <MiniStat label="Notices" value={h.notices ?? 0} tone={h.notices > 0 ? "danger" : "ok"} />
        <MiniStat label="Missing years" value={(h.missing_years || []).length} tone={(h.missing_years || []).length > 0 ? "warn" : "ok"} />
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">This year — anomalies</div>
          <ul className="mt-2 space-y-1.5">
            {anomalies.length === 0 && <li className="text-xs text-muted-foreground">Nothing flagged.</li>}
            {anomalies.map((a, i) => (
              <li key={i} className="text-xs flex items-start gap-2">
                {a.severity === "critical" ? <AlertTriangle className="h-3.5 w-3.5 text-rose-600 mt-0.5 flex-shrink-0" /> : <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />}
                <div><span className="font-medium">{a.label}</span> <span className="text-muted-foreground">— {a.detail}</span></div>
              </li>
            ))}
          </ul>
          {(intel.missing_docs || []).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Missing documents</div>
              <ul className="mt-1 space-y-0.5">
                {intel.missing_docs.map((m, i) => <li key={i} className="text-xs">• {m}</li>)}
              </ul>
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Recommendations</div>
          <ul className="mt-2 space-y-1.5">
            {recs.length === 0 && <li className="text-xs text-muted-foreground">No AI recommendations yet.</li>}
            {recs.map((r, i) => (
              <li key={i} className="text-xs flex items-start gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
          {(intel.risk_reasons || []).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Why this score</div>
              <ul className="mt-1 space-y-0.5">
                {intel.risk_reasons.map((r, i) => <li key={i} className="text-xs">• {r}</li>)}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function MiniStat({ label, value, tone }) {
  const border = tone === "danger" ? "border-rose-300" : tone === "warn" ? "border-amber-300" : "border-border";
  return (
    <div className={`rounded border ${border} bg-background p-2.5`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-xl font-bold">{value}</div>
    </div>
  );
}

// ================= Tasks board =================
function TasksBoard() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [newT, setNewT] = useState({ title: "", client_id: "", priority: "normal", due_date: "" });
  const [dlg, setDlg] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    const { data } = await api.get("/practice/tasks", { params });
    setTasks(data);
  };
  useEffect(() => {
    load();
    api.get("/practice/clients").then(({ data }) => setClients(data));
    /* eslint-disable-next-line */
  }, [statusFilter]);

  const create = async () => {
    if (!newT.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const payload = { ...newT };
      if (!payload.due_date) delete payload.due_date;
      if (!payload.client_id) delete payload.client_id;
      await api.post("/practice/tasks", payload);
      toast.success("Task created");
      setDlg(false); setNewT({ title: "", client_id: "", priority: "normal", due_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const updateStatus = async (id, status) => {
    await api.patch(`/practice/tasks/${id}`, { status });
    load();
  };

  const grouped = useMemo(() => {
    const g = { open: [], in_progress: [], blocked: [], done: [] };
    tasks.forEach(t => { (g[t.status] || g.open).push(t); });
    return g;
  }, [tasks]);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map(c => [c.id, c.name])),
    [clients]
  );

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto">
        <BackButton to="/practice" label="Practice" />
        <PageHeader>
          <PageHeader.Title>Task board</PageHeader.Title>
          <PageHeader.Actions>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 w-44"><SelectValue placeholder="Filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {TASK_STATUS.map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={dlg} onOpenChange={setDlg}>
              <DialogTrigger asChild><Button data-testid="new-task-btn"><Plus className="h-4 w-4 mr-1.5" />New task</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs">Title</Label><Input value={newT.title} onChange={(e) => setNewT({ ...newT, title: e.target.value })} className="h-10 mt-1.5" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Client</Label>
                      <Select value={newT.client_id || "none"} onValueChange={(v) => setNewT({ ...newT, client_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— No client —</SelectItem>
                          {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Priority</Label>
                      <Select value={newT.priority} onValueChange={(v) => setNewT({ ...newT, priority: v })}>
                        <SelectTrigger className="h-10 mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label className="text-xs">Due date</Label><Input type="date" value={newT.due_date} onChange={(e) => setNewT({ ...newT, due_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="h-10 mt-1.5" /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDlg(false)}>Cancel</Button>
                  <Button onClick={create} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </PageHeader.Actions>
        </PageHeader>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          {TASK_STATUS.map(s => (
            <Card key={s} className={`border-border p-4 ${COLUMN_ACCENT[s]}`}>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center justify-between">
                <span className="capitalize">{s.replace("_", " ")}</span>
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${COLUMN_COUNT_CLS[s]}`}>{grouped[s].length}</span>
              </div>
              <div className="mt-3 space-y-2 min-h-[60px]">
                {grouped[s].map(t => (
                  <div key={t.id} className="rounded border border-border p-3 bg-background hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium leading-snug">{t.title}</div>
                      <span className={`mt-0.5 h-2 w-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.normal}`} title={t.priority} />
                    </div>
                    {t.client_id && clientMap[t.client_id] && (
                      <div className="mt-1 text-[11px] text-primary/70 font-medium truncate">{clientMap[t.client_id]}</div>
                    )}
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {t.due_date ? `Due ${new Date(t.due_date).toLocaleDateString()}` : "No due date"}
                    </div>
                    <div className="mt-2">
                      <Select value={s} onValueChange={(next) => updateStatus(t.id, next)}>
                        <SelectTrigger className="h-7 text-[11px] w-full capitalize"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TASK_STATUS.map(st => (
                            <SelectItem key={st} value={st} className="text-xs capitalize">{st.replace(/_/g, " ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

// ================= Workflows =================
function Workflows() {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState({ template_key: "", client_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/practice/templates").then(({ data }) => setTemplates(data));
    api.get("/practice/clients").then(({ data }) => setClients(data));
  }, []);

  const apply = async () => {
    if (!sel.template_key || !sel.client_id) return toast.error("Pick a template and a client");
    setBusy(true);
    try {
      const { data } = await api.post("/practice/templates/apply", sel);
      toast.success(`${data.created} tasks created`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto">
        <BackButton to="/practice" label="Practice" />
        <PageHeader>
          <PageHeader.Title>Workflows</PageHeader.Title>
        </PageHeader>
        <p className="mt-2 text-sm text-muted-foreground">Reusable checklists — apply once, generate a full sequence of tasks with due dates.</p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          <div className="space-y-4">
            {templates.map(t => (
              <Card key={t.key} className="p-5 border-border">
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-lg flex items-center gap-2"><BookOpenCheck className="h-4 w-4 text-primary" />{t.name}</div>
                  <Badge variant="outline">{t.tasks.length} steps</Badge>
                </div>
                <ol className="mt-3 text-sm space-y-1.5">
                  {t.tasks.map((tk, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-muted-foreground w-16">D+{tk.days_from_start}</span>
                      <span className="flex-1">{tk.title}</span>
                      {tk.priority && <Badge variant="outline" className="text-[10px] uppercase">{tk.priority}</Badge>}
                    </li>
                  ))}
                </ol>
              </Card>
            ))}
          </div>

          <Card className="p-5 border-border h-fit sticky top-6">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Apply workflow</div>
            <div className="mt-3 space-y-3">
              <div>
                <Label className="text-xs">Template</Label>
                <Select value={sel.template_key} onValueChange={(v) => setSel({ ...sel, template_key: v })}>
                  <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{templates.map(t => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Client</Label>
                <Select value={sel.client_id} onValueChange={(v) => setSel({ ...sel, client_id: v })}>
                  <SelectTrigger className="h-10 mt-1.5"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={apply} disabled={busy} className="w-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply workflow"}</Button>
            </div>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}

// ================= Staff =================
function Staff() {
  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState({});
  const [invite, setInvite] = useState({ email: "", role: "associate" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/practice/staff").then(({ data }) => setStaff(data));
  useEffect(() => { load(); api.get("/practice/roles").then(({ data }) => setRoles(data)); }, []);

  const add = async () => {
    if (!invite.email.trim()) return toast.error("Email required");
    setBusy(true);
    try { await api.post("/practice/staff", invite); toast.success("Staff added"); setInvite({ email: "", role: "associate" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const changeRole = async (member_id, role) => {
    await api.patch(`/practice/staff/${member_id}`, { role });
    load();
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        <BackButton to="/practice" label="Practice" />
        <PageHeader>
          <PageHeader.Title>Staff &amp; Roles</PageHeader.Title>
        </PageHeader>

        <Card className="mt-5 p-4 border-border">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">Invite staff by email</Label>
              <Input value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="h-10 mt-1.5" placeholder="staff@example.com" />
            </div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                <SelectTrigger className="h-10 mt-1.5 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.filter(r => r !== "owner").map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={add} disabled={busy}><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">The person must have a platform account first (any role). We'll invite existing users only.</div>
        </Card>

        <Card className="mt-5 border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <tr><th className="text-left p-3">Member</th><th className="text-left p-3">Role</th><th className="text-left p-3">Joined</th><th></th></tr>
            </thead>
            <tbody>
              {staff.map(m => (
                <tr key={m.id} className="border-t border-border">
                  <td className="p-3">
                    <div className="font-medium">{m.user?.full_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{m.user?.email}</div>
                  </td>
                  <td className="p-3">
                    <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)} disabled={m.role === "owner"}>
                      <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(m.joined_at).toLocaleDateString()}</td>
                  <td className="p-3 text-right">
                    <details className="inline-block">
                      <summary className="text-xs cursor-pointer text-primary">Permissions</summary>
                      <div className="mt-2 flex flex-wrap gap-1 max-w-md">
                        {(m.permissions || roles[m.role] || []).map(p => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                      </div>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </AppShell>
  );
}
